import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import {
  Card,
  Button,
  Stack,
  Typography,
  Box,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useSnackbar } from "src/components/snackbar";
import FormProvider from "src/components/hook-form";
import { useLocales } from "src/locales";
import Iconify from "src/components/iconify";
import { kycApi } from "src/services/kycApi";

export default function DocumentsUploadSimplified({ handleNext, handleBack, uniqueId }) {
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [fetchingDocs, setFetchingDocs] = useState(true);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]); // Documents already in Buckzy
  const [newDocuments, setNewDocuments] = useState([]); // New documents to upload
  const [uploadingFiles, setUploadingFiles] = useState(new Set()); // Track files being uploaded

  // Document preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const methods = useForm();
  const { handleSubmit } = methods;

  // Fetch document types and load documents from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingDocs(true);

        // Fetch document types
        const typesRes = await kycApi.getDocumentTypes();
        setDocumentTypes(typesRes.data.data || []);

        // Fetch existing documents from API if uniqueId exists
        if (uniqueId) {
          try {
            console.log("Fetching documents for unique_key:", uniqueId);
            const docsRes = await kycApi.getDocuments(uniqueId);
            const documentsData = docsRes.data.data || docsRes.data || [];
            
            if (Array.isArray(documentsData)) {
              // Map API response to our document format
              const mappedDocuments = documentsData.map(doc => {
                console.log("🔍 Processing document in step 2:", doc);
                console.log("🔍 Available keys:", Object.keys(doc));
                
                // Parse metadata if it's a string
                let parsedMetadata = {};
                if (doc.metadata) {
                  if (typeof doc.metadata === 'string') {
                    try {
                      // Replace single quotes with double quotes for valid JSON
                      const jsonString = doc.metadata.replace(/'/g, '"');
                      parsedMetadata = JSON.parse(jsonString);
                    } catch (e) {
                      console.warn("Failed to parse metadata string:", e);
                      parsedMetadata = {};
                    }
                  } else if (typeof doc.metadata === 'object') {
                    parsedMetadata = doc.metadata;
                  }
                }
                
                // Try multiple possible field names for identifier
                let identifier = doc.identifier || 
                               doc.document_identifier || 
                               doc.identifier_name ||
                               doc.document_name ||
                               '';
                
                // If no identifier found, generate one from document_type_code and id
                if (!identifier) {
                  const docTypeCode = doc.document_type_code || 
                                    documentTypes.find(dt => dt.id === doc.document_type_id)?.buckzy_code || 
                                    'DOC';
                  const docId = doc.id || doc.document_id || '';
                  identifier = docId ? `${docTypeCode}_${docId}` : docTypeCode;
                }
                
                // Extract file_name from metadata or use fallback
                const fileName = doc.file_name || 
                               parsedMetadata.file_name || 
                               doc.name || 
                               doc.fileName || 
                               'Document';
                
                // Extract file_type from metadata
                const fileType = doc.file_type || parsedMetadata.file_type || null;
                
                console.log("🔍 Extracted identifier in step 2:", identifier);
                console.log("🔍 Extracted fileName in step 2:", fileName);
                
                return {
                  id: doc.document_id || doc.id,
                  document_id: doc.document_id || doc.id,
                  document_type_id: doc.document_type_id,
                  document_type_name: doc.document_type_name || documentTypes.find(dt => dt.id === doc.document_type_id)?.name || 'Unknown',
                  document_type_code: doc.document_type_code,
                  uploaded_file_id: doc.uploaded_file_id || doc.file_id,
                  file_id: doc.uploaded_file_id || doc.file_id,
                  url: doc.url || doc.file_url,
                  fileName: fileName,
                  name: fileName,
                  identifier: identifier,
                  metadata: parsedMetadata,
                  type: doc.document_type_id, // For compatibility
                  fileType: fileType,
                };
              });
              
              setExistingDocuments(mappedDocuments);
              console.log("✅ Loaded documents from API:", mappedDocuments);
            }
          } catch (error) {
            if (error?.response?.status === 404) {
              console.log("No documents found - starting fresh");
              setExistingDocuments([]);
            } else {
              console.error("Error fetching documents from API:", error);
              enqueueSnackbar("Failed to load documents", { variant: "warning" });
            }
          }
        }
        
        // Always show at least one empty row for new uploads
        setNewDocuments([{ id: Date.now(), type: "", file: null, fileName: "" }]);
      } catch (error) {
        console.error("Error fetching data:", error);
        enqueueSnackbar("Failed to load form data", { variant: "error" });
        // Even on error, show one empty row
        setNewDocuments([{ id: Date.now(), type: "", file: null, fileName: "" }]);
      } finally {
        setFetchingDocs(false);
      }
    };
    fetchData();
  }, [uniqueId, enqueueSnackbar]);

  // Add new document row
  const addNewDocument = () => {
    setNewDocuments([
      ...newDocuments,
      { id: Date.now(), type: "", file: null, fileName: "" },
    ]);
  };

  // Remove new document row (not yet uploaded)
  const removeNewDocument = (id) => {
    // Keep at least one row if no existing documents
    if (newDocuments.length === 1 && existingDocuments.length === 0) {
      enqueueSnackbar("At least one document is required", { variant: "warning" });
      return;
    }
    setNewDocuments(newDocuments.filter((doc) => doc.id !== id));
  };

  // Helper function to check if file is PDF or document file
  const isDocumentFile = (fileName, fileType) => {
    if (!fileName && !fileType) return false;
    
    const fileNameLower = (fileName || "").toLowerCase();
    const fileTypeLower = (fileType || "").toLowerCase();
    
    // Check for PDF
    if (fileNameLower.endsWith('.pdf') || fileTypeLower === 'application/pdf') {
      return true;
    }
    
    // Check for document files
    const documentExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.rtf', '.odt', '.ods'];
    const documentMimeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/rtf',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet'
    ];
    
    return documentExtensions.some(ext => fileNameLower.endsWith(ext)) ||
           documentMimeTypes.some(mime => fileTypeLower.includes(mime));
  };

  // Preview existing document from Buckzy (use url if available)
  const previewExistingDocument = async (doc) => {
    try {
      console.log("Previewing existing document:", doc);

      // If document has url from /file endpoint, check if it's a document file
      if (doc.url) {
        const fileName = doc.fileName || doc.filename || doc.file_name || doc.name || doc.identifier || "Document";
        const fileType = doc.fileType || null;
        
        // If it's a PDF or document file, open in new tab
        if (isDocumentFile(fileName, fileType)) {
          console.log("Opening document in new tab:", doc.url);
          window.open(doc.url, '_blank');
          return;
        }
        
        // Otherwise, show in modal (for images)
        console.log("Using URL from /file endpoint in modal:", doc.url);
        setLoadingPreview(true);
        setPreviewOpen(true);
        setPreviewDocument({
          name: doc.identifier || doc.name || `Document ${doc.id}`,
          type: doc.type,
          fileName: fileName,
          url: doc.url, // Use URL from /file endpoint
          isExisting: true,
          fileType: fileType
        });
        setLoadingPreview(false);
        return;
      }

      // Fallback: Fetch document content from backend API
      setLoadingPreview(true);
      setPreviewOpen(true);
      console.log("Fetching document preview from API for:", doc);

      const response = await kycApi.previewDocument(doc.id);

      console.log("Full response:", response);
      console.log("Response data:", response.data);
      console.log("Document data:", response.data.data);

      const docData = response.data.data;

      console.log("Available keys in docData:", Object.keys(docData || {}));
      console.log("docData.document:", docData.document ? "exists" : "missing");
      console.log("docData.file:", docData.file ? "exists" : "missing");

      // Create preview data
      setPreviewDocument({
        name: doc.identifier || `Document ${doc.id}`,
        type: doc.type,
        content: docData.document || docData.file || docData.base64 || docData.content, // Try different possible field names
        fileName: docData.fileName || doc.identifier,
        isExisting: true,
        url: null
      });
    } catch (error) {
      console.error("Error loading document preview:", error);
      enqueueSnackbar("Failed to load document preview", { variant: "error" });
      setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Preview new document (using url from /file endpoint)
  const previewNewDocument = (doc) => {
    if (!doc.url) {
      if (doc.file) {
        // Check if it's a document file
        if (isDocumentFile(doc.fileName, doc.file.type)) {
          // For document files without URL, we can't open in new tab, so show error
          enqueueSnackbar("Document preview not available. Please wait for upload to complete.", { variant: "warning" });
          return;
        }
        
        // Fallback: if url is not available but file is, use FileReader (for images)
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewDocument({
            name: doc.fileName,
            type: doc.type,
            content: e.target.result.split(',')[1], // Get base64 part
            fileName: doc.fileName,
            isExisting: false,
            fileType: doc.file.type,
            url: null
          });
          setPreviewOpen(true);
        };
        reader.readAsDataURL(doc.file);
      } else {
        enqueueSnackbar("No file available for preview", { variant: "warning" });
      }
      return;
    }

    // Check if it's a PDF or document file
    if (isDocumentFile(doc.fileName, doc.file?.type)) {
      // Open in new tab for PDFs and document files
      console.log("Opening document in new tab:", doc.url);
      window.open(doc.url, '_blank');
      return;
    }

    // Otherwise, show in modal (for images)
    setPreviewDocument({
      name: doc.fileName,
      type: doc.type,
      fileName: doc.fileName,
      isExisting: false,
      url: doc.url, // Use URL from /file endpoint
      fileType: doc.file?.type || null
    });
    setPreviewOpen(true);
  };

  // Close preview modal
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
  };

  // Delete existing document using DELETE /b2b/kyc/documents/
  const deleteExistingDocument = async (doc) => {
    try {
      // Validate that we have both uniqueId and document_id
      if (!uniqueId) {
        enqueueSnackbar("Unique key is required to delete document", { variant: "error" });
        return;
      }

      const documentId = doc.document_id || doc.id;
      if (!documentId) {
        enqueueSnackbar("Document ID is required to delete document", { variant: "error" });
        return;
      }

      enqueueSnackbar("Deleting document...", { variant: "info" });

      // Delete document using DELETE endpoint with unique_key and document_id
      await kycApi.deleteDocument(uniqueId, documentId);

      // Remove from state after successful deletion
      setExistingDocuments(existingDocuments.filter((d) => d.id !== doc.id));

      enqueueSnackbar("Document deleted successfully", { variant: "success" });

      // If all documents removed, add one empty row
      if (existingDocuments.length === 1 && newDocuments.length === 0) {
        setNewDocuments([{ id: Date.now(), type: "", file: null, fileName: "" }]);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.detail || "Failed to delete document";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleFileChange = async (id, file) => {
    if (!file) {
      // If no file, clear the document
      setNewDocuments(
        newDocuments.map((doc) =>
          doc.id === id
            ? { ...doc, file: null, fileName: "", file_id: null, url: null, uploadError: null }
            : doc
        )
      );
      return;
    }

    // Mark this file as uploading
    setUploadingFiles((prev) => new Set(prev).add(id));

    // Update document with file info immediately
    setNewDocuments(
      newDocuments.map((doc) =>
        doc.id === id
          ? { ...doc, file, fileName: file.name, file_id: null, url: null, uploadError: null }
          : doc
      )
    );

    try {
      // Step 1: Upload file to /file endpoint immediately
      const fileData = new FormData();
      fileData.append("file", file);
      fileData.append("is_private", true);
      
      // Add unique_key from unique_id if available
      if (uniqueId) {
        fileData.append("unique_key", uniqueId);
      }

      console.log("Uploading file to /file endpoint immediately:", file.name);
      const fileResponse = await kycApi.uploadFile(fileData);

      if (!fileResponse?.file_id || !fileResponse?.url) {
        throw new Error("File upload failed: No file_id or url returned");
      }

      const fileId = fileResponse.file_id;
      const fileUrl = fileResponse.url;

      console.log("File uploaded successfully:", {
        file_id: fileId,
        url: fileUrl,
        fileName: file.name
      });

      // Update document with file_id, url, and file type
      const updatedDoc = newDocuments.find(doc => doc.id === id);
      const updatedDocuments = newDocuments.map((doc) =>
        doc.id === id
          ? { ...doc, file, fileName: file.name, file_id: fileId, url: fileUrl, fileType: file.type, uploadError: null }
          : doc
      );
      setNewDocuments(updatedDocuments);

      enqueueSnackbar("File uploaded successfully", { variant: "success" });

      // If document type is already selected, submit document immediately
      if (updatedDoc && updatedDoc.type && uniqueId) {
        try {
          const docType = documentTypes.find((dt) => dt.id === updatedDoc.type);
          if (!docType) {
            console.error("Document type not found:", updatedDoc.type);
            return;
          }

          const payload = {
            unique_key: uniqueId,
            document_type_id: docType.id,
            uploaded_file_id: fileId,
            metadata: {
              file_name: file.name,
              file_type: file.type,
            }
          };

          console.log("Adding document immediately after file upload:", payload);
          const response = await kycApi.addDocument(payload);
          const statusCode = response?.status || response?.response?.status;

          if (statusCode === 201) {
            // Document added successfully, move to existing documents
            const responseData = response?.data?.data || response?.data || {};
            console.log("📄 Add document API response:", responseData);
            
            const newExistingDoc = {
              id: responseData.document_id || Date.now(),
              document_id: responseData.document_id,
              document_type_id: docType.id,
              document_type_name: docType.name,
              uploaded_file_id: fileId,
              file_id: fileId,
              url: fileUrl,
              fileName: file.name,
              name: file.name,
              fileType: file.type,
              metadata: payload.metadata,
              type: docType.id,
              identifier: responseData.identifier || 
                         responseData.document_identifier || 
                         responseData.name ||
                         (responseData.metadata && responseData.metadata.identifier) ||
                         '',
            };

            // Remove from newDocuments and add to existingDocuments
            setNewDocuments(updatedDocuments.filter(doc => doc.id !== id));
            setExistingDocuments([...existingDocuments, newExistingDoc]);

            enqueueSnackbar("Document added successfully!", { variant: "success" });
          } else {
            throw new Error("Unexpected response status");
          }
        } catch (error) {
          console.error("Error adding document:", error);
          const errorMessage = error?.response?.data?.message || error?.message || "Failed to add document";
          enqueueSnackbar(`Failed to add document: ${errorMessage}`, { variant: "error" });
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to upload file";
      
      // Update document with error
      setNewDocuments(
        newDocuments.map((doc) =>
          doc.id === id
            ? { ...doc, file, fileName: file.name, file_id: null, url: null, uploadError: errorMessage }
            : doc
        )
      );

      enqueueSnackbar(`Failed to upload file: ${errorMessage}`, { variant: "error" });
    } finally {
      // Remove from uploading set
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleTypeChange = async (id, type) => {
    // Update the document type in state
    const updatedDoc = newDocuments.find(doc => doc.id === id);
    const updatedDocuments = newDocuments.map((doc) => (doc.id === id ? { ...doc, type } : doc));
    setNewDocuments(updatedDocuments);

    // If document has both type and file_id (file already uploaded), submit immediately
    if (updatedDoc && updatedDoc.file_id && updatedDoc.url && type && uniqueId) {
      try {
        const docType = documentTypes.find((dt) => dt.id === type);
        if (!docType) {
          console.error("Document type not found:", type);
          return;
        }

        const payload = {
          unique_key: uniqueId,
          document_type_id: docType.id,
          uploaded_file_id: updatedDoc.file_id,
          metadata: {
            file_name: updatedDoc.fileName,
            file_type: updatedDoc.fileType || null,
          }
        };

        console.log("Adding document immediately:", payload);
        const response = await kycApi.addDocument(payload);
        const statusCode = response?.status || response?.response?.status;

        if (statusCode === 201) {
          // Document added successfully, move to existing documents
          const responseData = response?.data?.data || response?.data || {};
          console.log("📄 Add document API response (type change):", responseData);
          
          const newExistingDoc = {
            id: responseData.document_id || Date.now(),
            document_id: responseData.document_id,
            document_type_id: docType.id,
            document_type_name: docType.name,
            uploaded_file_id: updatedDoc.file_id,
            file_id: updatedDoc.file_id,
            url: updatedDoc.url,
            fileName: updatedDoc.fileName,
            name: updatedDoc.fileName,
            fileType: updatedDoc.fileType,
            metadata: payload.metadata,
            type: docType.id,
            identifier: responseData.identifier || 
                       responseData.document_identifier || 
                       responseData.name ||
                       (responseData.metadata && responseData.metadata.identifier) ||
                       '',
          };

          // Remove from newDocuments and add to existingDocuments
          setNewDocuments(updatedDocuments.filter(doc => doc.id !== id));
          setExistingDocuments([...existingDocuments, newExistingDoc]);

          enqueueSnackbar("Document added successfully!", { variant: "success" });
        } else {
          throw new Error("Unexpected response status");
        }
      } catch (error) {
        console.error("Error adding document:", error);
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to add document";
        enqueueSnackbar(`Failed to add document: ${errorMessage}`, { variant: "error" });
      }
    }
  };

  // Helper function to get document type name from Buckzy code
  const getDocumentTypeName = (buckzyCode) => {
    const docType = documentTypes.find(
      (dt) => dt.buckzy_code === buckzyCode || dt.id === buckzyCode
    );
    return docType ? docType.name : buckzyCode; // Fallback to code if not found
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      // Check if we have any documents at all
      if (existingDocuments.length === 0) {
        enqueueSnackbar("Please upload at least one document", {
          variant: "error",
        });
        setLoading(false);
        return;
      }

      // Check if there are any incomplete documents (with file but no type, or with type but no file)
      const incompleteDocs = newDocuments.filter((doc) => 
        (doc.type && !doc.file_id) || (doc.file_id && !doc.type)
      );

      if (incompleteDocs.length > 0) {
        enqueueSnackbar("Please complete all document uploads (both file and document type required)", {
          variant: "error",
        });
        setLoading(false);
        return;
      }

      // All documents are already submitted (they're in existingDocuments)
      // Just proceed to next step
      enqueueSnackbar("Documents ready!", { variant: "success" });
      handleNext();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to proceed", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetchingDocs) {
    return (
      <Card sx={{ p: 3, mt: 4 }}>
        <Typography>Loading documents...</Typography>
      </Card>
    );
  }

  console.log("existingDocuments", existingDocuments);
  console.log("newDocuments", newDocuments);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Card sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {"Upload Documents"}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Upload required documents (Passport, ID, Business License, etc.)
        </Alert>

        <Stack spacing={3}>
          {/* Existing Documents from Buckzy */}
          {existingDocuments.length > 0 && (
            <>
              <Typography variant="subtitle2" color="primary">
                Uploaded Documents ({existingDocuments.length})
              </Typography>
              {existingDocuments.map((doc, index) => (
                <Box
                  key={`existing-${index}`}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "success.main",
                    borderRadius: 1,
                    bgcolor: "success.lighter",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Iconify
                      icon="eva:checkmark-circle-2-fill"
                      sx={{ color: "success.main", width: 24, height: 24 }}
                    />

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {getDocumentTypeName(doc.type) || doc.documentType || `Document ${index + 1}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doc.fileName || doc.filename || doc.file_name || doc.name || doc.identifier || "Document file"}
                      </Typography>
                    </Box>

                    <IconButton
                      color="primary"
                      onClick={() => previewExistingDocument(doc)}
                      title="Preview document"
                      sx={{ mr: 1 }}
                    >
                      <Iconify icon="eva:eye-outline" />
                    </IconButton>

                    <IconButton
                      color="error"
                      onClick={() => deleteExistingDocument(doc)}
                      title="Remove document"
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </>
          )}

          {/* New Documents to Upload */}
          <>
            {newDocuments.length > 0 ? (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                  {existingDocuments.length > 0 ? "Add More Documents" : "Upload New Documents"}
                </Typography>
              {newDocuments.map((doc, index) => (
                <Box
                  key={doc.id}
                  sx={{
                    p: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 80 }}>
                      New {index + 1}
                    </Typography>

                    <TextField
                      select
                      size="small"
                      label="Document Type"
                      value={doc.type}
                      onChange={(e) => handleTypeChange(doc.id, e.target.value)}
                      sx={{ flex: 1 }}
                    >
                      {documentTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<Iconify icon="eva:cloud-upload-fill" />}
                      sx={{ minWidth: 150 }}
                    >
                      {doc.fileName ? "Change File" : "Upload File"}
                      <input
                        type="file"
                        hidden
                        onChange={(e) =>
                          handleFileChange(doc.id, e.target.files[0])
                        }
                        accept="image/*,.pdf"
                      />
                    </Button>

                    {doc.fileName && (
                      <Typography variant="caption" sx={{ flex: 1 }}>
                        {doc.fileName}
                        {uploadingFiles.has(doc.id) && (
                          <Typography component="span" variant="caption" color="info.main" sx={{ ml: 1 }}>
                            (Uploading...)
                          </Typography>
                        )}
                        {doc.uploadError && (
                          <Typography component="span" variant="caption" color="error.main" sx={{ ml: 1, display: "block" }}>
                            {doc.uploadError}
                          </Typography>
                        )}
                        {doc.url && (
                          <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                            ✓ Uploaded
                          </Typography>
                        )}
                      </Typography>
                    )}

                    {doc.url && (
                      <IconButton
                        color="primary"
                        onClick={() => previewNewDocument(doc)}
                        title="Preview document"
                        sx={{ mr: 1 }}
                        disabled={uploadingFiles.has(doc.id)}
                      >
                        <Iconify icon="eva:eye-outline" />
                      </IconButton>
                    )}
                    {doc.file && !doc.url && !uploadingFiles.has(doc.id) && (
                      <IconButton
                        color="warning"
                        onClick={() => handleFileChange(doc.id, doc.file)}
                        title="Retry upload"
                        sx={{ mr: 1 }}
                      >
                        <Iconify icon="eva:refresh-outline" />
                      </IconButton>
                    )}

                    <IconButton
                      color="error"
                      onClick={() => removeNewDocument(doc.id)}
                      disabled={newDocuments.length === 1 && existingDocuments.length === 0}
                      title="Remove document"
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
              </>
            ) : null}

            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={addNewDocument}
              sx={{ mt: 2 }}
            >
              {existingDocuments.length > 0 ? "Add More Documents" : "Add Document"}
            </Button>
          </>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={handleBack} disabled={loading}>
            {t("back")}
          </Button>
          <LoadingButton type="submit" variant="contained" loading={loading}>
            {t("continue")}
          </LoadingButton>
        </Stack>
      </Card>

      {/* Document Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {loadingPreview ? "Loading document..." : previewDocument?.fileName || "Document Preview"}
        </DialogTitle>
        <DialogContent>
          {loadingPreview ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <Typography>Loading document preview...</Typography>
            </Box>
          ) : previewDocument ? (
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
              bgcolor: "grey.100",
              borderRadius: 1,
              p: 2
            }}>
              {previewDocument.url ? (
                // Use URL from /file endpoint
                <>
                  {previewDocument.fileType === "application/pdf" || previewDocument.fileName?.toLowerCase().endsWith(".pdf") ? (
                    <iframe
                      src={previewDocument.url}
                      style={{
                        width: "100%",
                        height: "600px",
                        border: "none"
                      }}
                      title={previewDocument.fileName}
                    />
                  ) : (
                    <img
                      src={previewDocument.url}
                      alt={previewDocument.fileName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "600px",
                        objectFit: "contain"
                      }}
                      onError={(e) => {
                        // If image fails, try as iframe (might be PDF)
                        e.target.style.display = "none";
                        const iframe = document.createElement("iframe");
                        iframe.src = previewDocument.url;
                        iframe.style.width = "100%";
                        iframe.style.height = "600px";
                        iframe.style.border = "none";
                        e.target.parentElement.appendChild(iframe);
                      }}
                    />
                  )}
                </>
              ) : previewDocument.content ? (
                // Fallback to base64 content
                <>
                  {previewDocument.fileType === "application/pdf" || previewDocument.fileName?.toLowerCase().endsWith(".pdf") ? (
                    <iframe
                      src={`data:application/pdf;base64,${previewDocument.content}`}
                      style={{
                        width: "100%",
                        height: "600px",
                        border: "none"
                      }}
                      title={previewDocument.fileName}
                    />
                  ) : (
                    <img
                      src={`data:image/jpeg;base64,${previewDocument.content}`}
                      alt={previewDocument.fileName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "600px",
                        objectFit: "contain"
                      }}
                      onError={(e) => {
                        // If image fails, try PDF
                        e.target.style.display = "none";
                        const iframe = document.createElement("iframe");
                        iframe.src = `data:application/pdf;base64,${previewDocument.content}`;
                        iframe.style.width = "100%";
                        iframe.style.height = "600px";
                        iframe.style.border = "none";
                        e.target.parentElement.appendChild(iframe);
                      }}
                    />
                  )}
                </>
              ) : (
                <Typography color="text.secondary">No preview available</Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
